import {RoomPlannerProductWithTemplate} from 'components/customer/RoomPlanner/types';
import {store} from 'store/customer';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {Material, MaterialType} from 'components/customer/Materials/entity';
import {mapMaterial} from 'components/customer/Materials/helper/materialHelper';
import {
    LegacyDoorInterface,
    mapDoor,
} from 'components/customer/Materials/helper/doorHelper';
import {detectShakerDoor} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';

type CabinetDoor = ReturnType<typeof getDoor> | null | undefined;

/**
 * Resolves the cabinet door from store or backup data
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {CabinetDoor} doorFromStore - Door data from store
 * @param {unknown} [backupDoor] - Backup door data
 * @return {CabinetDoor} Resolved door configuration
 */
export const resolveCabinetDoor = (
    values: RoomPlannerProductWithTemplate,
    doorFromStore: CabinetDoor,
    backupDoor?: unknown
): CabinetDoor => {
    if (backupDoor) {
        const legacyDoor: LegacyDoorInterface = {
            ...(backupDoor as LegacyDoorInterface),
            advanced:
                values.job_cabinet_door_advanced != null
                    ? Number(values.job_cabinet_door_advanced) === 1
                    : ((backupDoor as LegacyDoorInterface).advanced ??
                      (backupDoor as {is_advanced?: boolean}).is_advanced),
        };
        return mapDoor(legacyDoor);
    }

    const cabinetDoorId =
        values.cabinet_door ?? values.door_style ?? values.door?.id;
    const cabinetDoorName = values.cabinet_door_name ?? values.door?.name;

    // Live product-editor updates sync cabinet_door to the store selection first.
    if (
        doorFromStore &&
        ((cabinetDoorId != null &&
            doorFromStore.id === Number(cabinetDoorId)) ||
            (cabinetDoorName && doorFromStore.name === cabinetDoorName))
    ) {
        return doorFromStore;
    }

    if (values.door) {
        const legacyDoor: LegacyDoorInterface = {
            ...values.door,
            advanced:
                values.job_cabinet_door_advanced != null
                    ? Number(values.job_cabinet_door_advanced) === 1
                    : values.door.advanced,
        };
        return mapDoor(legacyDoor);
    }

    return doorFromStore;
};

/**
 * Resolves the cabinet material from store or backup data
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {Material | null | undefined} materialFromStore - Material data from store
 * @param {unknown} [backupMaterial] - Backup material data
 * @return {Material | null | undefined} Resolved material configuration
 */
export const resolveCabinetMaterial = (
    values: RoomPlannerProductWithTemplate,
    materialFromStore: Material | null | undefined,
    backupMaterial?: unknown
): Material | null | undefined => {
    if (backupMaterial) {
        return backupMaterial as Material;
    }

    if (values.ext_material) {
        return mapMaterial(values.ext_material);
    }

    return materialFromStore;
};

/**
 * Checks if the product is being restored from undo/redo or backup
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {boolean} True if undo/redo or backup restore
 */
export const isUndoOrBackupRestore = (values: RoomPlannerProductWithTemplate) =>
    Boolean(
        (values as {fromUndoOrRedo?: boolean}).fromUndoOrRedo ||
        (values as {fromBackup?: boolean}).fromBackup
    );

/**
 * Gets shaker door information including material, door, and wireframe mode state
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {object} Object containing shaker door info, material, showTexture, and door
 */
export const getShakerDoorInfo = (values: RoomPlannerProductWithTemplate) => {
    const state = store.getState();
    const materialFromStore = getMaterial(state, MaterialType.EXTERIOR, 0);
    const doorFromStore = getDoor(state, MaterialType.EXTERIOR, 0);

    const valuesWithShaker = values as unknown as {
        material?: unknown;
        door?: unknown;
    };
    const useHistoryBackup = isUndoOrBackupRestore(values);

    const material = resolveCabinetMaterial(
        values,
        materialFromStore,
        useHistoryBackup ? valuesWithShaker.material : undefined
    );
    const door = resolveCabinetDoor(
        values,
        doorFromStore,
        useHistoryBackup ? valuesWithShaker.door : undefined
    );

    const shakerDoorInfo = detectShakerDoor(material, door);

    return {
        shakerDoorInfo,
        material,
        door,
        showTexture: !state.roomPlanner.isWireframeMode,
    };
};
